import React, {useCallback, useEffect, useMemo} from "react";
import axios, {CancelToken} from "axios";
import useIsMounted from "../use-is-mounted";
import BroadcastService from "../../../core/services/broadcast";
import {GetPartnerInformationArgs, usePartnerInformation, usePartnerInformationController} from "../use-partner-information";
import LocalStorageService from "../../../core/services/cache/local_storage_service";
import Api from "../../../core/services/api/api";

export type GetPartnerColorThemeArgs = GetPartnerInformationArgs;

export interface UsePartnerColorThemeArgs {
    autoFetch?: boolean;
    autoFetchArgs?: GetPartnerColorThemeArgs;
}

export interface UsePartnerColorThemeReturn {
    loading: boolean,
    partnerColorTheme: Record<string, unknown>;
    setPartnerColorTheme: (colorTheme: Record<string, unknown>) => Promise<void>;
    getPartnerColorTheme: (args?: GetPartnerColorThemeArgs, cancelToken?: CancelToken,) => Promise<void>;
}

/**
 * Uses the color theme of the current partner and listens for any changes to it.
 * It also provides a function to update the color theme of the partner.
 * @param autoFetch
 * @param autoFetchArgs
 */
const usePartnerColorTheme = ({autoFetch = false, autoFetchArgs = {}}: UsePartnerColorThemeArgs = {}): UsePartnerColorThemeReturn => {
    const {getPartnerInformation, setCachedPartnerInformation} = usePartnerInformationController();
    const [loading, setLoading] = React.useState(autoFetch);
    const partnerInformation = usePartnerInformation();
    const currentColorTheme = useMemo(() => {
        const val = partnerInformation?.colorThemeJson ?? LocalStorageService.get(LocalStorageService.keys.partnerColorTheme);
        try {
            return JSON.parse(val ?? '{}');
        } catch {
            return {}
        }
    }, [partnerInformation?.colorThemeJson]);
    const isMounted = useIsMounted();

    /**
     * Fetches the color theme and logo url of the current partner
     * @type {(function(*): Promise<void>)}
     */
    const getPartnerColorTheme = useCallback(async (args: GetPartnerColorThemeArgs = {}, cancelToken?: CancelToken) => {
        setLoading(true);
        await getPartnerInformation(args, cancelToken);
        if (!isMounted())
            return;
        setLoading(false);
    }, [getPartnerInformation, isMounted])

    /**
     * Listens for the color theme fetched event and sets the color theme
     */
    useEffect(() => {
        if (!autoFetch) {
            return;
        }
        const cancelTokenSource = axios.CancelToken.source();
        void getPartnerColorTheme({
            customAuthToken: autoFetchArgs?.customAuthToken,
            logger: autoFetchArgs?.logger,
            disregardCache: autoFetchArgs?.disregardCache,
            setGlobalState: autoFetchArgs?.setGlobalState,
        }, cancelTokenSource.token);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [getPartnerColorTheme, autoFetch, autoFetchArgs?.setGlobalState, autoFetchArgs?.customAuthToken, autoFetchArgs?.logger, autoFetchArgs?.disregardCache]);

    /**
     * As soon as the component mounts:
     * - set the flag for re-generating the color theme of the application.
     */
    useEffect(() => {
        const {close} = BroadcastService.addEventListener(BroadcastService.channelNames.colorThemeFetched, "message", (colorThemeString) => {
            LocalStorageService.set(LocalStorageService.keys.partnerColorTheme, colorThemeString)
            setCachedPartnerInformation(prevState => !prevState ? null : ({
                ...prevState,
                colorThemeJson: colorThemeString,
            }))
        })
        return () => {
            close?.();
        }
    }, [setCachedPartnerInformation])

    /**
     * Sets the color theme of the current partner
     * @type {(function(*): Promise<void>)|*}
     */
    const setPartnerColorTheme = useCallback(async (colorTheme) => {
        const colorThemeString = JSON.stringify(colorTheme);
        const response = await Api.updatePartnerColor({
            colorThemeJson: colorThemeString,
        })
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCachedPartnerInformation(prevState => !prevState ? null : ({
                ...prevState,
                colorThemeJson: colorThemeString,
            }))
            LocalStorageService.set(LocalStorageService.keys.partnerColorTheme, colorThemeString)
            BroadcastService.postMessage(BroadcastService.channelNames.colorThemeFetched, colorThemeString);
        }
    }, [isMounted, setCachedPartnerInformation])

    return React.useMemo(() => ({
        loading,
        partnerColorTheme: currentColorTheme,
        getPartnerColorTheme,
        setPartnerColorTheme,
    }), [
        loading,
        currentColorTheme,
        getPartnerColorTheme,
        setPartnerColorTheme,
    ])
};

export default usePartnerColorTheme;